import React, { useState, useRef, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  LinearProgress,
  Grid,
  Box,
  RadioGroup,
  Radio,
  FormControlLabel,
  Paper,
  Divider,
  Checkbox,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import MicIcon from "@material-ui/icons/Mic";
import StopIcon from "@material-ui/icons/Stop";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useBroadcastContext } from "../../context/Broadcast/BroadcastContext";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: 550,
    maxWidth: 700,
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: 13,
    color: theme.palette.primary.main,
    textTransform: "uppercase",
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  boxContainer: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? "#1e293b" : "#f8fafc",
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  logBox: {
    maxHeight: 140,
    overflowY: "auto",
    fontFamily: "monospace",
    fontSize: 11,
    padding: theme.spacing(1),
    backgroundColor: theme.palette.type === "dark" ? "#0b1120" : "#111827",
    color: "#e2e8f0",
    borderRadius: 6,
    marginTop: theme.spacing(1.5),
  },
  audioPreview: {
    width: "100%",
    height: 36,
    marginTop: theme.spacing(1),
  },
}));

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getRandomDelay = (minSec, maxSec) => {
  const min = Math.min(minSec, maxSec);
  const max = Math.max(minSec, maxSec);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const BroadcastModal = ({ open, onClose, selectedContacts = [] }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [mode, setMode] = useState(selectedContacts.length > 0 ? "selected" : "tickets");
  const [manualNumbers, setManualNumbers] = useState("");
  const [csvContacts, setCsvContacts] = useState([]);
  const [csvFileName, setCsvFileName] = useState("");

  // Suporte à seleção de Conversas / Tickets
  const [ticketStatus, setTicketStatus] = useState("open");
  const [ticketList, setTicketList] = useState([]);
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);

  const [messageText, setMessageText] = useState("");
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState("");
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState("");
  const [mediaType, setMediaType] = useState("");

  // Configs Anti-ban
  const [minDelay, setMinDelay] = useState(20);
  const [maxDelay, setMaxDelay] = useState(45);
  const [batchSize, setBatchSize] = useState(15);
  const [batchPauseMin, setBatchPauseMin] = useState(5);

  // Estado Global do Disparo
  const {
    isRunning,
    isPaused,
    progress,
    stats,
    currentContact,
    countdown,
    logs: globalLogs,
    startBroadcast: startGlobalBroadcast,
    stopBroadcast,
    togglePause,
    openMonitor,
    addLog,
  } = useBroadcastContext();

  // Gravação de áudio ao vivo
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTimer, setRecordingTimer] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);

  const fetchTickets = async (status = ticketStatus) => {
    try {
      setLoadingTickets(true);
      const { data } = await api.get("/tickets", {
        params: {
          status: status,
          showAll: "true",
          pageNumber: 1,
        },
      });
      const list = data.tickets || [];
      setTicketList(list);
      setSelectedTicketIds(list.map((t) => t.id));
    } catch (err) {
      toast.error("Erro ao carregar atendimentos/conversas.");
    } finally {
      setLoadingTickets(false);
    }
  };

  useEffect(() => {
    if (open && mode === "tickets") {
      fetchTickets(ticketStatus);
    }
  }, [open, mode, ticketStatus]);

  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const handleToggleTicket = (ticketId) => {
    setSelectedTicketIds((prev) =>
      prev.includes(ticketId)
        ? prev.filter((id) => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const handleSelectAllTickets = () => {
    if (selectedTicketIds.length === ticketList.length) {
      setSelectedTicketIds([]);
    } else {
      setSelectedTicketIds(ticketList.map((t) => t.id));
    }
  };



    const startRecordingAudio = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const options = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? { mimeType: "audio/webm;codecs=opus" }
        : undefined;

      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        const file = new File([blob], "audio_gravado.webm", { type: blob.type });
        setAudioFile(file);
        setAudioPreviewUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((track) => track.stop());
        addLog("🎙️ Áudio gravado com sucesso! Será entregue como gravação ao vivo (PTT).", "#34d399");
      };

      recorder.start();
      setIsRecording(true);
      setRecordingTimer(0);
      timerIntervalRef.current = setInterval(() => {
        setRecordingTimer((t) => t + 1);
      }, 1000);
      addLog("🔴 Gravando áudio pelo microfone...", "#fbbf24");
    } catch (err) {
      toast.error("Não foi possível acessar o microfone.");
    }
  };

  const stopRecordingAudio = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerIntervalRef.current);
    }
  };

  const handleAudioChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAudioFile(file);
      setAudioPreviewUrl(URL.createObjectURL(file));
      addLog(`🎵 Áudio selecionado: ${file.name}. Será enviado como PTT nativo.`, "#34d399");
    }
  };

  const removeAudio = () => {
    setAudioFile(null);
    setAudioPreviewUrl("");
  };

  const handleMediaChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMediaFile(file);
      setMediaPreviewUrl(URL.createObjectURL(file));
      if (file.type.startsWith("image/")) {
        setMediaType("image");
      } else if (file.type.startsWith("video/")) {
        setMediaType("video");
      } else {
        setMediaType("document");
      }
      addLog(`📎 Mídia anexada: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`, "#38bdf8");
    }
  };

  const removeMedia = () => {
    setMediaFile(null);
    setMediaPreviewUrl("");
    setMediaType("");
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      const lines = content.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
      const parsed = [];

      lines.forEach((line, index) => {
        const cols = line.split(/[,;\t]/).map((c) => c.replace(/["']/g, "").trim());
        if (index === 0 && (cols[0].toLowerCase().includes("tel") || cols[0].toLowerCase().includes("contato") || cols[0].toLowerCase().includes("phone"))) {
          return;
        }

        let num = "";
        let name = "";
        cols.forEach((c) => {
          const clean = c.replace(/\D/g, "");
          if (clean.length >= 8 && !num) {
            num = clean;
          } else if (c.length > 1 && !name && isNaN(c)) {
            name = c;
          }
        });

        if (num) {
          parsed.push({ number: num, name: name });
        }
      });

      setCsvContacts(parsed);
      addLog(`📁 Planilha importada com ${parsed.length} contatos válidos.`, "#38bdf8");
    };
    reader.readAsText(file);
  };

  const getRecipientList = () => {
    let rawList = [];
    if (mode === "tickets") {
      rawList = ticketList
        .filter((t) => selectedTicketIds.includes(t.id))
        .map((t) => ({
          id: t.contactId,
          ticketId: t.id,
          number: t.contact?.number ? t.contact.number.replace(/\D/g, "") : "",
          name: t.contact?.name || "",
        }));
    } else if (mode === "selected") {
      rawList = selectedContacts.map((c) => ({
        id: c.id,
        number: c.number.replace(/\D/g, ""),
        name: c.name,
      }));
    } else if (mode === "csv") {
      rawList = csvContacts;
    } else {
      const lines = manualNumbers.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);
      lines.forEach((line) => {
        const parts = line.split(/[,;\t]/).map((p) => p.trim());
        const rawNum = parts[0] ? parts[0].replace(/\D/g, "") : "";
        const name = parts[1] || "";
        if (rawNum.length >= 8) {
          rawList.push({ number: rawNum, name: name });
        }
      });
    }

    // Deduplicação dentro da mesma campanha (evita disparar 2x para o mesmo número no mesmo disparo)
    const seen = new Set();
    const uniqueList = [];
    for (const item of rawList) {
      const clean = String(item.number).replace(/\D/g, "");
      const norm = (clean.length === 10 || clean.length === 11) ? `55${clean}` : clean;
      if (!seen.has(norm)) {
        seen.add(norm);
        uniqueList.push(item);
      }
    }
    return uniqueList;
  };

  const handleStartBroadcast = () => {
    const recipients = getRecipientList();
    if (recipients.length === 0) {
      toast.error("Nenhum contato encontrado para envio.");
      return;
    }

    if (!messageText.trim() && !audioFile && !mediaFile) {
      toast.error("Por favor, digite uma mensagem de texto, selecione um áudio ou anexe uma imagem/vídeo.");
      return;
    }

    startGlobalBroadcast({
      recipients,
      messageText,
      mediaFile,
      audioFile,
      minDelay,
      maxDelay,
      batchSize,
      pauseMinutes: batchPauseMin,
    });

    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const totalContacts = getRecipientList().length;

  return (
    <Dialog open={open} onClose={handleClose} classes={{ paper: classes.dialogPaper }} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" style={{ fontWeight: 700 }}>
          🚀 Disparador Inteligente & Áudios Humanizados (Anti-Ban)
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography className={classes.sectionTitle}>1. Origem dos Destinatários</Typography>
        <div className={classes.boxContainer}>
          <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
            <FormControlLabel value="tickets" control={<Radio color="primary" />} label="Conversas / Tickets" />
            {selectedContacts.length > 0 && (
              <FormControlLabel
                value="selected"
                control={<Radio color="primary" />}
                label={`Contatos Selecionados (${selectedContacts.length})`}
              />
            )}
            <FormControlLabel value="manual" control={<Radio color="primary" />} label="Colar Números" />
            <FormControlLabel value="csv" control={<Radio color="primary" />} label="Subir Planilha .CSV" />
          </RadioGroup>

          {mode === "tickets" && (
            <Box mt={1}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap">
                <RadioGroup row value={ticketStatus} onChange={(e) => setTicketStatus(e.target.value)}>
                  <FormControlLabel value="open" control={<Radio size="small" color="primary" />} label="Em Atendimento (Abertos)" />
                  <FormControlLabel value="pending" control={<Radio size="small" color="secondary" />} label="Aguardando (Pendentes)" />
                </RadioGroup>
                <Button size="small" variant="outlined" color="primary" onClick={() => fetchTickets(ticketStatus)} disabled={loadingTickets}>
                  {loadingTickets ? "Carregando..." : "Atualizar Conversas"}
                </Button>
              </Box>

              {loadingTickets ? (
                <LinearProgress style={{ margin: "12px 0" }} />
              ) : ticketList.length === 0 ? (
                <Typography variant="body2" color="textSecondary" style={{ padding: 8 }}>
                  Nenhuma conversa encontrada na aba {ticketStatus === "open" ? "Em Atendimento" : "Aguardando"}.
                </Typography>
              ) : (
                <>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          indeterminate={selectedTicketIds.length > 0 && selectedTicketIds.length < ticketList.length}
                          checked={ticketList.length > 0 && selectedTicketIds.length === ticketList.length}
                          onChange={handleSelectAllTickets}
                        />
                      }
                      label={<Typography variant="body2" style={{ fontWeight: 600 }}>Marcar Todos ({selectedTicketIds.length}/{ticketList.length} selecionados)</Typography>}
                    />
                  </Box>
                  <Paper variant="outlined" style={{ maxHeight: 150, overflowY: "auto", padding: "4px 8px" }}>
                    {ticketList.map((t) => (
                      <Box key={t.id} display="flex" alignItems="center" justifyContent="space-between" style={{ borderBottom: "1px solid rgba(0,0,0,0.05)", padding: "2px 0" }}>
                        <Box display="flex" alignItems="center" style={{ overflow: "hidden" }}>
                          <Checkbox
                            size="small"
                            color="primary"
                            checked={selectedTicketIds.includes(t.id)}
                            onChange={() => handleToggleTicket(t.id)}
                          />
                          <Typography variant="body2" noWrap style={{ fontWeight: 600, marginRight: 8 }}>
                            {t.contact?.name || "Sem nome"}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" noWrap>
                            ({t.contact?.number})
                          </Typography>
                        </Box>
                        {t.queue?.name && (
                          <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, backgroundColor: t.queue?.color || "#6366f1", color: "#fff", whiteSpace: "nowrap" }}>
                            {t.queue.name}
                          </span>
                        )}
                      </Box>
                    ))}
                  </Paper>
                </>
              )}
            </Box>
          )}

          {mode === "selected" && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              {selectedContacts.length} contatos selecionados previamente da tabela de contatos.
            </Typography>
          )}

          {mode === "manual" && (
            <TextField
              label="Cole telefones (um por linha, com DDD)"
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              placeholder="11999998888, João&#10;21988887777, Maria"
              value={manualNumbers}
              onChange={(e) => setManualNumbers(e.target.value)}
              helperText="Exemplo: 11999998888 ou 11999998888, Nome do Aluno"
              style={{ marginTop: 8 }}
            />
          )}

          {mode === "csv" && (
            <Box mt={1}>
              <input type="file" accept=".csv,.txt" id="csv-input-whaticket" style={{ display: "none" }} onChange={handleCsvUpload} />
              <label htmlFor="csv-input-whaticket">
                <Button variant="outlined" color="primary" component="span">
                  Selecionar Planilha (.CSV)
                </Button>
              </label>
              {csvFileName && (
                <Typography variant="caption" style={{ marginLeft: 12 }}>
                  Arquivo: <b>{csvFileName}</b> ({csvContacts.length} contatos)
                </Typography>
              )}
            </Box>
          )}

          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 8 }}>
            Total de destinatários na fila: <b>{totalContacts}</b>
          </Typography>
        </div>

        <Typography className={classes.sectionTitle}>2. Conteúdo do Disparo</Typography>
        <div className={classes.boxContainer}>
          <Box mb={2}>
            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
              Áudio Nativo (Gravado na hora - PTT Verde):
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              O cliente recebe o áudio como gravação ao vivo (com microfone verde, sem etiqueta de encaminhado) e status "gravando áudio...".
            </Typography>
            <Box mt={1} display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              {!isRecording ? (
                <Button
                  variant="contained"
                  size="small"
                  style={{ backgroundColor: "#ef4444", color: "#fff", fontWeight: "bold" }}
                  onClick={startRecordingAudio}
                >
                  <MicIcon fontSize="small" style={{ marginRight: 4 }} /> Gravar Áudio Agora
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  style={{ backgroundColor: "#dc2626", color: "#fff", fontWeight: "bold" }}
                  onClick={stopRecordingAudio}
                >
                  <StopIcon fontSize="small" style={{ marginRight: 4 }} /> Parar Gravação ({recordingTimer}s)
                </Button>
              )}

              <input type="file" accept="audio/*,.mp3,.ogg,.m4a" id="audio-input-modal" style={{ display: "none" }} onChange={handleAudioChange} />
              <label htmlFor="audio-input-modal">
                <Button variant="outlined" size="small" component="span" color="primary">
                  Ou Escolher Arquivo (.mp3 / .ogg)
                </Button>
              </label>

              {audioFile && (
                <Button size="small" color="secondary" onClick={removeAudio} style={{ marginLeft: 8 }}>
                  Remover
                </Button>
              )}
            </Box>
            {audioPreviewUrl && (
              <audio controls src={audioPreviewUrl} className={classes.audioPreview} />
            )}
          </Box>

          <Divider style={{ margin: "14px 0" }} />

          <Box mb={2}>
            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
              Anexar Imagem, Vídeo ou Documento:
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              Fotos (.jpg, .png), vídeos curtos (.mp4) ou documentos (.pdf).
            </Typography>
            <Box mt={1} display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
              <input
                type="file"
                accept="image/*,video/*,.pdf"
                id="media-input-modal"
                style={{ display: "none" }}
                onChange={handleMediaChange}
              />
              <label htmlFor="media-input-modal">
                <Button variant="outlined" size="small" component="span" color="primary">
                  <AttachFileIcon fontSize="small" style={{ marginRight: 4 }} />
                  Escolher Imagem / Vídeo / PDF
                </Button>
              </label>

              {mediaFile && (
                <Button size="small" color="secondary" onClick={removeMedia} style={{ marginLeft: 8 }}>
                  Remover Mídia
                </Button>
              )}
            </Box>
            {mediaPreviewUrl && (
              <Box mt={1}>
                {mediaType === "image" && (
                  <img
                    src={mediaPreviewUrl}
                    alt="Preview"
                    style={{ maxHeight: 120, maxWidth: "100%", borderRadius: 6, border: "1px solid #ccc" }}
                  />
                )}
                {mediaType === "video" && (
                  <video
                    src={mediaPreviewUrl}
                    controls
                    style={{ maxHeight: 140, maxWidth: "100%", borderRadius: 6 }}
                  />
                )}
                {mediaType === "document" && (
                  <Typography variant="caption" style={{ padding: "4px 8px", backgroundColor: "#e2e8f0", borderRadius: 4, display: "inline-block" }}>
                    📄 {mediaFile.name} ({(mediaFile.size / 1024 / 1024).toFixed(2)} MB)
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Divider style={{ margin: "14px 0" }} />

          <Box mt={2}>
            <TextField
              label="Mensagem de Texto / Legenda da Mídia"
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              placeholder="Oi {nome}, tudo bem? Aqui é da coordenação pedagógica..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              helperText="Use a tag {nome} para personalizar o primeiro nome de cada contato. Se anexar foto/vídeo, o texto vira legenda."
            />
          </Box>
        </div>

        <Typography className={classes.sectionTitle}>3. Parâmetros Anti-Bloqueio (Anti-Ban)</Typography>
        <div className={classes.boxContainer}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Delay Mín (seg)"
                type="number"
                size="small"
                fullWidth
                variant="outlined"
                value={minDelay}
                onChange={(e) => setMinDelay(Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Delay Máx (seg)"
                type="number"
                size="small"
                fullWidth
                variant="outlined"
                value={maxDelay}
                onChange={(e) => setMaxDelay(Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Lote (envios)"
                type="number"
                size="small"
                fullWidth
                variant="outlined"
                value={batchSize}
                onChange={(e) => setBatchSize(Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Pausa (min)"
                type="number"
                size="small"
                fullWidth
                variant="outlined"
                value={batchPauseMin}
                onChange={(e) => setBatchPauseMin(Number(e.target.value))}
              />
            </Grid>
          </Grid>
        </div>

        {/* PROGRESSO E LOGS */}
        {isRunning && (
          <Box mt={2}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {isPaused
                  ? "⏸️ Disparo Pausado"
                  : countdown > 0
                  ? `⏳ Aguardando ${countdown}s (simulando humano)...`
                  : `🚀 Enviando (${stats.current}/${stats.total})...`}
              </Typography>
              <Typography variant="caption" style={{ fontWeight: 700, color: "#10b981" }}>
                {progress}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}

        {globalLogs.length > 0 && (
          <div className={classes.logBox}>
            {globalLogs.map((log, index) => (
              <div key={index} style={{ color: log.color }}>
                [{log.timestamp}] {log.text}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {isRunning ? (
          <>
            <Button onClick={togglePause} color="default" variant="outlined">
              {isPaused ? "Retomar" : "Pausar"}
            </Button>
            <Button onClick={stopBroadcast} color="secondary" variant="contained">
              Interromper
            </Button>
            <Button onClick={handleClose} color="primary">
              Fechar Modal (Acompanhar no Canto)
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} color="default">
              Cancelar
            </Button>
            <Button onClick={handleStartBroadcast} color="primary" variant="contained">
              Iniciar Disparo Seguro ({totalContacts})
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BroadcastModal;
